import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API_BASE } from "../config";
import { useSummit } from "../pages/summitdetails"; // ← use context, not useParams
import {
  RiFileTextLine,
  RiCheckLine,
  RiPhoneLine,
  RiMailLine,
  RiArrowRightLine,
  RiDownloadLine,
  RiPrinterLine,
} from "react-icons/ri";
import toast from "react-hot-toast";

/* ─── THEME COLORS ───────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
};

/* ─── CSS ──────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-guidelines-wrapper{padding:40px clamp(20px,4vw,64px) 56px;background:${T.offWhite}}
  .t-guidelines-container{max-width:1320px;margin:0 auto}
  .t-sidebar{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:24px;box-shadow:${T.shadow};position:sticky;top:24px}
  .t-sidebar-title{font-size:18px;font-weight:700;color:${T.navy};margin-bottom:16px;display:flex;align-items:center;gap:8px}
  .t-sidebar-nav{list-style:none;margin:0;padding:0}
  .t-sidebar-nav-item{width:100%;text-align:left;padding:10px 14px;border-radius:12px;font-size:14px;color:${T.textMid};transition:all 0.2s;cursor:pointer;display:flex;align-items:center;gap:10px;background:none;border:none;font-family:'Inter',sans-serif}
  .t-sidebar-nav-item:hover{background:${T.softTeal};transform:translateX(4px);color:${T.primary}}
  .t-sidebar-nav-item.active{background:${T.softTeal};color:${T.primary};font-weight:500}
  .t-guideline-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};overflow:hidden;box-shadow:${T.shadow};transition:all 0.3s ease;margin-bottom:20px;cursor:pointer}
  .t-guideline-card:hover{box-shadow:${T.shadowMd};border-color:${T.primaryLight}}
  .t-guideline-header{display:flex;align-items:flex-start;gap:16px;padding:24px}
  .t-guideline-icon{width:52px;height:52px;border-radius:16px;background:${T.softTeal};display:flex;align-items:center;justify-content:center;font-size:24px;flex-shrink:0}
  .t-guideline-title{font-size:18px;font-weight:700;color:${T.text};margin-bottom:4px}
  .t-guideline-order{font-size:11px;padding:2px 8px;border-radius:20px;background:${T.softTeal};color:${T.primary}}
  .t-guideline-desc{font-size:14px;color:${T.textMuted};line-height:1.65;margin-top:8px}
  .t-guideline-body{padding:0 24px 24px 92px}
  .t-contact-card{background:${T.gradPrimary};border-radius:20px;padding:32px;text-align:center;margin-top:32px}
  .t-footer-note{text-align:center;padding-top:32px;margin-top:32px;border-top:1px solid ${T.borderLight}}

  .t-action-buttons{display:flex;flex-wrap:wrap;justify-content:center;gap:12px;margin-top:24px;padding:0 clamp(20px,4vw,64px)}
  .t-btn{display:inline-flex;align-items:center;gap:8px;padding:10px 24px;border-radius:40px;font-size:14px;font-weight:600;cursor:pointer;transition:all 0.2s;border:none;font-family:'Inter',sans-serif}
  .t-btn-primary{background:${T.white};color:${T.primary};border:1px solid ${T.border}}
  .t-btn-primary:hover{transform:scale(1.05);box-shadow:${T.shadowMd};border-color:${T.primary}}
  .t-copy-btn{font-size:12px;padding:6px 16px;border-radius:20px;background:${T.offWhite};color:${T.primary};border:none;cursor:pointer;transition:all 0.2s;font-family:'Inter',sans-serif}
  .t-copy-btn:hover{background:${T.softTeal}}

  .t-gl-grid{display:grid;gap:24px;grid-template-columns:1fr}
  @media(min-width:1024px){.t-gl-grid{grid-template-columns:1fr 2fr}}

  .rotate-90{transform:rotate(90deg)}
  .transition-transform{transition:transform 0.3s ease}

  .t-sw{max-width:1320px;margin:0 auto;display:flex;flex-direction:column;align-items:center;text-align:center;padding:72px 24px;gap:12px}
  .t-s-icon{width:52px;height:52px;border-radius:13px;background:${T.softTeal};border:1px solid ${T.border};display:flex;align-items:center;justify-content:center;font-size:22px;color:${T.primary};margin-bottom:4px}
  .t-s-title{font-family:'Playfair Display',serif;font-size:20px;font-weight:800;color:${T.navy}}
  .t-s-sub{font-size:14px;color:${T.textMuted};max-width:300px;line-height:1.6}
  .t-s-btn{margin-top:4px;padding:9px 20px;border-radius:8px;border:1px solid ${T.border};background:${T.white};color:${T.text};font-family:'Inter',sans-serif;font-size:13px;font-weight:500;cursor:pointer;transition:all .15s}
  .t-s-btn:hover{border-color:${T.primary};color:${T.primary}}

  @media(max-width:768px){
    .t-guideline-header{flex-direction:column}
    .t-guideline-body{padding:0 20px 20px 20px}
    .t-sidebar{position:relative;top:0;margin-bottom:24px}
  }

  @keyframes spin{to{transform:rotate(360deg)}}
`;

/* ════════════════════════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════════════════════════ */
const SubmissionGuidelines = () => {
  // ✅ Use context to get the real MongoDB _id — NOT useParams
  const { eventId } = useSummit();

  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGuideline, setExpandedGuideline] = useState(null);

  useEffect(() => {
    if (eventId) {
      fetchActiveGuidelines();
    } else {
      setError("No event specified");
      setLoading(false);
    }
  }, [eventId]);

  const fetchActiveGuidelines = async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `${API_BASE}/api/submission-guidelines/active/${eventId}`;
      console.log("Fetching guidelines from:", url);

      const response = await axios.get(url);

      if (response.data.success && response.data.data) {
        setGuidelines(response.data.data);
        setError(null);
      } else {
        setError(response.data.message || "No active guidelines found for this event");
      }
    } catch (err) {
      console.error("Error fetching guidelines:", err);
      if (err.response?.status === 404) {
        setError("Submission guidelines not available for this event yet.");
      } else {
        setError(err.response?.data?.message || "Failed to load submission guidelines");
      }
      toast.error("Failed to load submission guidelines");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${guidelines?.title || "Submission Guidelines"}</title>
        <meta charset="UTF-8">
        <style>
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:'Segoe UI','Inter',-apple-system,BlinkMacSystemFont,sans-serif;max-width:900px;margin:0 auto;padding:40px;color:#1C3461;line-height:1.6}
          h1{color:#2A9D8F;border-bottom:3px solid #2A9D8F;padding-bottom:12px;margin-bottom:20px;font-size:28px}
          .description{font-size:16px;color:#2D4A5A;margin-bottom:30px;line-height:1.7}
          .guideline-item{margin-bottom:24px;padding:20px;background:#E8F5F3;border-radius:12px;border-left:4px solid #2A9D8F;page-break-inside:avoid;break-inside:avoid}
          .guideline-icon{font-size:28px;margin-bottom:12px}
          .guideline-title{font-size:18px;font-weight:700;color:#1A3A4A;margin-bottom:8px}
          .guideline-order{display:inline-block;background:#C8E8E4;padding:2px 10px;border-radius:20px;font-size:11px;color:#1F7A6E;margin-left:10px}
          .guideline-desc{font-size:14px;color:#2D4A5A;line-height:1.6}
          .footer{margin-top:50px;padding-top:20px;border-top:1px solid #C8E8E4;text-align:center;color:#6B8090;font-size:12px}
          .contact-section{margin-top:40px;padding:20px;background:#2A9D8F;color:white;border-radius:12px;text-align:center}
          .contact-section h3{margin-bottom:10px}
          @media print{body{padding:20px}.guideline-item{break-inside:avoid;page-break-inside:avoid}}
        </style>
      </head>
      <body>
        <h1>${guidelines?.title || "Submission Guidelines"}</h1>
        <div class="description">${guidelines?.description || ""}</div>
        ${(guidelines?.guidelines || [])
          .sort((a, b) => a.order - b.order)
          .map(item => `
          <div class="guideline-item">
            <div class="guideline-icon">${item.icon || "📝"}</div>
            <div class="guideline-title">${item.title}<span class="guideline-order">#${item.order}</span></div>
            <div class="guideline-desc">${item.description}</div>
          </div>
        `).join('')}
        <div class="contact-section">
          <h3>Need Help?</h3>
          <p>Email: submissions@meetings21.com | Phone: +1 (555) 123-4567</p>
        </div>
        <div class="footer">
          <p>Generated on ${new Date().toLocaleDateString()} | Version: ${guidelines?.version || "1.0"}</p>
          <p>© ${new Date().getFullYear()} All Rights Reserved</p>
        </div>
      </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    toast.success("Print window opened!");
  };

  const handleDownload = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${guidelines?.title || "Submission Guidelines"}</title>
        <meta charset="UTF-8">
        <style>
          *{margin:0;padding:0;box-sizing:border-box}
          body{font-family:'Segoe UI','Inter',-apple-system,BlinkMacSystemFont,sans-serif;max-width:900px;margin:0 auto;padding:40px;color:#1C3461;line-height:1.6}
          h1{color:#2A9D8F;border-bottom:3px solid #2A9D8F;padding-bottom:12px;margin-bottom:20px;font-size:28px}
          .description{font-size:16px;color:#2D4A5A;margin-bottom:30px;line-height:1.7}
          .guideline-item{margin-bottom:24px;padding:20px;background:#E8F5F3;border-radius:12px;border-left:4px solid #2A9D8F}
          .guideline-icon{font-size:28px;margin-bottom:12px}
          .guideline-title{font-size:18px;font-weight:700;color:#1A3A4A;margin-bottom:8px}
          .guideline-order{display:inline-block;background:#C8E8E4;padding:2px 10px;border-radius:20px;font-size:11px;color:#1F7A6E;margin-left:10px}
          .guideline-desc{font-size:14px;color:#2D4A5A;line-height:1.6}
          .footer{margin-top:50px;padding-top:20px;border-top:1px solid #C8E8E4;text-align:center;color:#6B8090;font-size:12px}
          .contact-section{margin-top:40px;padding:20px;background:#2A9D8F;color:white;border-radius:12px;text-align:center}
          .contact-section h3{margin-bottom:10px}
        </style>
      </head>
      <body>
        <h1>${guidelines?.title || "Submission Guidelines"}</h1>
        <div class="description">${guidelines?.description || ""}</div>
        ${(guidelines?.guidelines || [])
          .sort((a, b) => a.order - b.order)
          .map(item => `
          <div class="guideline-item">
            <div class="guideline-icon">${item.icon || "📝"}</div>
            <div class="guideline-title">${item.title}<span class="guideline-order">#${item.order}</span></div>
            <div class="guideline-desc">${item.description}</div>
          </div>
        `).join('')}
        <div class="contact-section">
          <h3>Need Help?</h3>
          <p>Email: submissions@meetings21.com | Phone: +1 (555) 123-4567</p>
        </div>
        <div class="footer">
          <p>Generated on ${new Date().toLocaleDateString()} | Version: ${guidelines?.version || "1.0"}</p>
          <p>© ${new Date().getFullYear()} All Rights Reserved</p>
        </div>
      </body>
      </html>
    `;
    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${guidelines?.title || "submission-guidelines"}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Guidelines downloaded!");
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiFileTextLine size={13} /> Meetings21 · Author Information</div>
              <h1 className="t-hero-h">Submission <em>Guidelines</em></h1>
              <p className="t-hero-p">Loading guidelines...</p>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
                <div style={{ width: 8, height: 8, background: T.accentGold, transform: "rotate(45deg)" }} />
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-guidelines-wrapper">
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div style={{ width: 48, height: 48, border: `3px solid ${T.border}`, borderTopColor: T.primary, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 16px" }} />
            <p style={{ color: T.textMuted }}>Loading submission guidelines...</p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Error / empty state ── */
  if (error || !guidelines) {
    return (
      <div style={{ background: T.offWhite, minHeight: "100vh" }}>
        <style>{css}</style>
        <div className="t-hero">
          <div className="t-hero-i">
            <div>
              <div className="t-hero-lbl"><RiFileTextLine size={13} /> Meeting21 · Author Information</div>
              <h1 className="t-hero-h">Submission <em>Guidelines</em></h1>
              <p className="t-hero-p">Essential information for authors submitting to Meeting21</p>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
                <div style={{ width: 8, height: 8, background: T.accentGold, transform: "rotate(45deg)" }} />
                <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
              </div>
            </div>
          </div>
        </div>
        <div className="t-sw">
          <div className="t-s-icon"><RiFileTextLine size={24} /></div>
          <p className="t-s-title">No Guidelines Available</p>
          <p className="t-s-sub">{error || "Submission guidelines will be available soon."}</p>
          <button className="t-s-btn" onClick={fetchActiveGuidelines}>Refresh</button>
        </div>
      </div>
    );
  }

  const sortedGuidelines = [...(guidelines.guidelines || [])].sort((a, b) => a.order - b.order);
  const totalGuidelines = sortedGuidelines.length;

  /* ── Main render ── */
  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ── HERO ── */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiFileTextLine size={13} />
              Meetings21 · Author Information
            </div>
            <h1 className="t-hero-h">Submission <em>Guidelines</em></h1>
            <p className="t-hero-p">
              Essential information for authors submitting to Meetings21. Please review these
              guidelines carefully before preparing your submission.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 24 }}>
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
              <div style={{ width: 8, height: 8, background: T.accentGold, transform: "rotate(45deg)" }} />
              <div style={{ flex: 1, height: 2, background: `linear-gradient(90deg,transparent,${T.primary}40,transparent)` }} />
            </div>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">{totalGuidelines}</span>
              <span className="t-st-l">Guidelines</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">{guidelines.version || "1.0"}</span>
              <span className="t-st-l">Version</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">2025</span>
              <span className="t-st-l">Edition</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Action Buttons ── */}
      <div className="t-action-buttons">
        <button onClick={handleDownload} className="t-btn t-btn-primary">
          <RiDownloadLine size={18} /> Download as HTML
        </button>
        <button onClick={handlePrint} className="t-btn t-btn-primary">
          <RiPrinterLine size={18} /> Print Guidelines
        </button>
      </div>

      {/* ── Main Content ── */}
      <div className="t-guidelines-wrapper">
        <div className="t-guidelines-container">
          <div className="t-gl-grid">

            {/* Sidebar */}
            <div>
              <div className="t-sidebar">
                <h3 className="t-sidebar-title">
                  <RiCheckLine size={18} style={{ color: T.primary }} />
                  Quick Navigation
                </h3>
                <ul className="t-sidebar-nav">
                  {sortedGuidelines.map((item, index) => (
                    <li key={index}>
                      <button
                        onClick={() => {
                          const el = document.getElementById(`guideline-${index}`);
                          if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "start" });
                            setExpandedGuideline(index);
                          }
                        }}
                        className={`t-sidebar-nav-item ${expandedGuideline === index ? "active" : ""}`}
                      >
                        <span>{item.icon || "📝"}</span>
                        <span style={{ flex: 1 }}>{item.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>

                <div style={{ marginTop: 24, paddingTop: 24, borderTop: `1px solid ${T.borderLight}` }}>
                  <p style={{ fontSize: 12, color: T.textMuted }}>
                    Last Updated:{" "}
                    {new Date(guidelines.updatedAt).toLocaleDateString("en-US", {
                      year: "numeric", month: "long", day: "numeric",
                    })}
                  </p>
                  <p style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
                    Version: {guidelines.version || "1.0"}
                  </p>
                </div>
              </div>
            </div>

            {/* Cards */}
            <div>
              {sortedGuidelines.map((item, index) => (
                <motion.div
                  key={index}
                  id={`guideline-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  className="t-guideline-card"
                  onClick={() => setExpandedGuideline(expandedGuideline === index ? null : index)}
                >
                  <div className="t-guideline-header">
                    <div className="t-guideline-icon">{item.icon || "📝"}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                        <h3 className="t-guideline-title">{item.title}</h3>
                        <span className="t-guideline-order">#{item.order}</span>
                      </div>
                      <p className="t-guideline-desc">
                        {expandedGuideline === index
                          ? item.description
                          : `${item.description.substring(0, 120)}${item.description.length > 120 ? "..." : ""}`}
                      </p>
                    </div>
                    <RiArrowRightLine
                      size={20}
                      style={{ color: T.primaryLight, flexShrink: 0 }}
                      className={`transition-transform ${expandedGuideline === index ? "rotate-90" : ""}`}
                    />
                  </div>

                  {expandedGuideline === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      className="t-guideline-body"
                    >
                      <p style={{ fontSize: 15, color: T.textMid, lineHeight: 1.7, marginBottom: 16 }}>
                        {item.description}
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(item.description);
                          toast.success("Guideline copied!");
                        }}
                        className="t-copy-btn"
                      >
                        Copy Guideline
                      </button>
                    </motion.div>
                  )}
                </motion.div>
              ))}

              {/* Contact card */}
              <div className="t-contact-card">
                <h3 style={{ fontSize: 20, fontWeight: 700, color: T.white, marginBottom: 12 }}>
                  Need Help?
                </h3>
                <p style={{ color: `${T.white}90`, marginBottom: 20 }}>
                  If you have any questions about the submission guidelines, please contact us
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.white }}>
                    <RiMailLine size={18} />
                    <span>submissions@meeting21.com</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.white }}>
                    <RiPhoneLine size={18} />
                    <span>+1 (555) 123-4567</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionGuidelines;